import { useContext, useEffect, useState } from "react";
import { AppContext } from "../AppContextProvider";
import secretsService from "../services/secretsService";

// Module-level cache — keeps repeated opens of a deal's Map tab from
// re-fetching the same secret. Lives as long as the browser tab.
let cachedToken = null;

/**
 * The same secret as a promise, sharing the hook's cache — for callers that
 * need the token outside a component (the report map widget, via the reports
 * data source). `authToken` is the caller's bearer token.
 */
export const loadMapboxToken = async (authToken) => {
  if (cachedToken) return cachedToken;
  const secret = await secretsService.getSecret(authToken, "mapbox-token");
  cachedToken = (secret || "").trim();
  return cachedToken;
};

/**
 * Fetches the `mapbox-token` secret from Azure Key Vault via the proxy's
 * /secrets endpoint and caches it for the session.
 *
 * @returns {{ token: string|null, loading: boolean, errorMsg: string|null }}
 */
const useMapboxToken = () => {
  const { token, getToken, isTokenExpired } = useContext(AppContext);
  const [state, setState] = useState(() =>
    cachedToken
      ? { token: cachedToken, loading: false, errorMsg: null }
      : { token: null, loading: true, errorMsg: null }
  );

  useEffect(() => {
    if (cachedToken) return;
    let cancelled = false;

    const fetchToken = async () => {
      try {
        let myToken = token;
        if (!myToken || isTokenExpired(myToken)) {
          myToken = await getToken();
        }
        const loaded = await loadMapboxToken(myToken);
        if (cancelled) return;
        setState({ token: loaded, loading: false, errorMsg: null });
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load mapbox-token from Key Vault:", err);
        setState({
          token: null,
          loading: false,
          errorMsg: err?.message || String(err),
        });
      }
    };

    fetchToken();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
};

export default useMapboxToken;
